import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import moment from 'moment/moment';
import Title from '../../../../commonComponents/Title';
import translate from '../../../../commonComponents/translate';
import ContentContainer from '../../../../commonComponents/pageLayout/ContentContainer';
import CareerFilters from './CareerFilters';
import GlobalSummary from './GlobalSummary';
import SubHeader from '../../../../commonComponents/pageLayout/SubHeader';
import Notifications from '../../../../services/notifications';
import Loader from '../../../../services/loader';
import TopicBanner from '../../../../commonComponents/pageLayout/header/TopicBanner';
import PvEvolution from './PvEvolution';
import LevelObjectives from './LevelObjectives';
import UserModal from './modals/UserModal';
import { useModal } from '../../../../services/customHooks';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import { getStartDateAndEndDateInterval } from '../../../../services/dateUtilityFunctions';

function MyCareer(props) {
  const { t, _csrf } = props;

  const loader = useRef(new Loader('#mivitana-my-career'));
  const [isLoading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [levelObjectives, setLevelObjectives] = useState([]);

  const [userModal, setUserModal] = useModal({
    show: false,
    data: null,
  });

  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);

  const [globalSummaryData, setGlobalSummaryData] = useState({
    userId: '',
    isActive: true,
    activeUntilDate: '',
    current5RollingWeekVA: 0,
    currentCommissionPercentage: 0,
    rightLegRollingVA: 0,
    leftLegRollingVA: 0,
    forecastRightLegVA: 0,
    forecastLeftLegVA: 0,
    nextPayout: 0,
    lastWeekPayout: 0,
    currencyInfo: {},
    activeVAGoal: 100,
  });

  const { organization } = useContext(ConfigsContext);
  const { directSelling } = organization;

  // List — initial range in the plan timezone (BRAVON-6325), matching the
  // date-picker presets.
  const [initialStartDate, initialEndDate] =
    getStartDateAndEndDateInterval(directSelling);
  const startOfMonth = initialStartDate.valueOf();
  const today = initialEndDate.valueOf();

  const [filters, setFilters] = useState({
    user: null,
    userNumber: null,
    startDate: startOfMonth,
    endDate: today,
  });

  const handleFiltersChange = useCallback(
    field => value =>
      setFilters(prevState => ({ ...prevState, [field]: value })),
    [setFilters],
  );
  const handleDateChange = (startDate, endDate) => {
    setFilters(prevState => ({
      ...prevState,
      startDate,
      endDate,
    }));
  };

  const handleGetGlobalSummary = useCallback(() => {
    axios
      .get('/api/tailor-made/mivitana/summary/global', {
        params: filters,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setGlobalSummaryData(response.data);
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        console.log(err);
        Notifications.showErrorFromRequest(err, 'error_fetchGlobalSummaryData');
      });
  }, [filters]);

  const handleGetPVEvolution = useCallback(() => {
    axios
      .get('/api/tailor-made/mivitana/pvEvolution', {
        params: filters,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setChartData(
          response.data.map(report => ({
            ...report,
            weekLabel: `${report.year} - ${t('report.mivitana.week')} ${
              report.week
            }`,
          })),
        );
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        console.log(err);
        Notifications.showErrorFromRequest(err, 'error_fetchGlobalSummaryData');
      });
  }, [filters]);

  const handleGetObjectives = useCallback(() => {
    axios
      .get('/api/tailor-made/mivitana/level/objectives', {
        params: filters,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setLevelObjectives(response.data);
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        console.log(err);
        Notifications.showErrorFromRequest(err, 'error_fetchGlobalSummaryData');
      });
  }, [filters]);

  useEffect(() => {
    handleGetGlobalSummary();
    handleGetObjectives();
    handleGetPVEvolution();
  }, [filters]);

  const showUserModal = () => {
    setUserModal({
      show: true,
      data: null,
    });
  };

  return (
    <>
      {userModal.show ? (
        <UserModal onClose={() => setUserModal({ show: false, data: null })} />
      ) : null}
      <Title title={t('header_myCareer')}>
        <ContentContainer>
          <SubHeader title={t('header_myCareer')} />
          <TopicBanner />
          <div className="m-content" id="mivitana-my-career">
            <div className="full-page-container-with-subheader">
              <CareerFilters
                t={t}
                onDateChange={handleDateChange}
                onFilterChange={handleFiltersChange}
                filters={filters}
              />
              <div className="career-summaries">
                <GlobalSummary
                  t={t}
                  data={globalSummaryData}
                  showUserModal={showUserModal}
                />
              </div>
              <div className="evolution-graph">
                <PvEvolution data={chartData} />
              </div>
              <div>
                <LevelObjectives data={levelObjectives} />
              </div>
            </div>
          </div>
        </ContentContainer>
      </Title>
    </>
  );
}

MyCareer.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
};

export default translate(MyCareer);
